import { createEmailField, RadioFieldModel, StringFieldModel } from "@/common/commonForms";
import { PhoneInputModel } from "@/components-v2/PhoneInput";
import { isOk, isPending, ok, pending, PhoneNumber, Result } from "@/common/common";
import {
  copyToClipboard,
  alertAction,
  confirmAction,
  DCEvent,
  EventDispatcher,
  httpDelete,
  HttpEvent,
  httpGet,
  httpPost,
  HttpResponse,
  loadUrl,
  navigate,
  showError,
  showGeneralError,
  showSuccess,
  showVerifyEmailDialog,
  thunk,
  showGeneralSuccess,
} from "@/events/eventsDomain";
import { norwegianPersonalIdValidator } from "@/utils/other";
import { RootStoreData } from "@/events/rootStoreData";
import { AdditionalUserWithEmail, ApiKey, GetUserSettingsResponse, User } from "@/types/UserInfo";
import { EditableFieldProps } from "@/components-v2/EditableField";
import { TagProps } from "@/components-v2/Tag";
import { ButtonProps, IconButtonProps, LinkButtonProps } from "@/components-v2/Button";
import { formatPhoneNumber } from "@/utils/phoneNumbers";
import { portfolioUrl, userSettingsTabUrl } from "@/utils/navigation.ts";
import AlertMessageStore from "@/stores/AlertMessageStore.ts";
import { TabButtonProps } from "@/components-v2/Tabs.tsx";
import { compact, get, uniqBy } from "lodash";
import { isAxiosError } from "@/api";
import { TMessage } from "@/i18n/tt.ts";
import { getFullName } from "@/utils/Roles.ts";
import { Language, toSupportedLanguage } from "@/i18n/languages.ts";
import { DataTableProps, RowAction, TableColumnValue } from "@/components-v2/DataTable.tsx";
import { QueryParams, singleValue, toQueryString } from "@/utils/queryParams";
import {
  createSsoConnectedAlert,
  createSsoDomainNotActivatedAlert,
  isSsoConnectedProvider,
} from "@/Login/loginDomain";
import { TMessageKey } from "@/i18n/types.gen.ts";
import { accessLabels } from "@/types/enums/AccessType.ts";
import { InfoButtonProps } from "@/components-v2/InfoButton.tsx";

export type UserSettingsTab = "account" | "additional-emails" | "api-key";
export type UsernameType = "SOCIAL_SECURITY_NUMBER" | "EMAIL";
export type UserLoginMethodType = "BANKID" | "USERNAME" | "MICROSOFT" | "GOOGLE";

export type UserLoginMethod = {
  method: UserLoginMethodType;
  lastUsed?: string;
  isConnected: boolean;
};

export type EmailForm = { email: StringFieldModel; isSaving?: boolean };
export type PhoneNumberForm = { phoneNumber: PhoneInputModel; isSaving?: boolean };

export type PasswordForm = {
  oldPassword: StringFieldModel;
  newPassword: StringFieldModel;
  confirmedPassword: StringFieldModel;
  isSaving?: boolean;
};

export type PasswordFieldProps =
  | {
      mode: "show";
      hasPassword: boolean;
      isEmailAvailable: boolean;
      hasCreatedPassword: boolean;
      isLoading: boolean;
      onEdit: () => void;
      createPassword: () => void;
    }
  | { mode: "edit"; form: PasswordForm; onSave: () => void; onCancel: () => void };

export type ShowPasswordProps = Extract<PasswordFieldProps, { mode: "show" }>;

export type EditPasswordProps = Extract<PasswordFieldProps, { mode: "edit" }>;

export type UserLoginMethodsResponse = { loginMethods: UserLoginMethod[] };

export type UpdateEmailRequest = { email: string };

export type UpdateUsernameRequest = { usernameType: UsernameType };

export type UpdatePhoneNumberRequest = { phoneNumber: PhoneNumber };

export type ChangePasswordRequest = { oldPassword: string; newPassword: string };

export type ApplyCreatePasswordRequest = {
  ref?: string;
  username?: string;
  password?: string;
};

export type UserSettingsState = {
  referrer?: string;
  userName?: string;
  isLoading?: boolean;
  tab?: UserSettingsTab;
  response?: GetUserSettingsResponse;
  loginMethods?: UserLoginMethod[];
  emailForm?: EmailForm;
  phoneNumberForm?: PhoneNumberForm;
  savingUsernameType?: UsernameType;
  passwordForm?: PasswordForm;
  createPasswordResult?: Result;
  deletingSsoMethod?: UserLoginMethodType;
  newApiKey?: ApiKey;
  isCreatingApiKey?: boolean;
  deletingApiKey?: string;
};

export type UserSettingsProps = {
  messageStore: AlertMessageStore;
  userSettingsTab: UserSettingsTab;
  name: string;
  navigateBack: DCEvent[];
  tabs: TabButtonProps[];
  state: UserSettingsState;
  user: User | null;
  dispatch: EventDispatcher;
  refreshUser: () => void;
  additionalEmailInfo?: TMessage;
  usernameTypeModel: RadioFieldModel<UsernameType>;
  isNorwegianAuthenticatedCountry?: boolean;
  openCreteApiDialog: DCEvent[];
};

export const usernameTypes: UsernameType[] = ["SOCIAL_SECURITY_NUMBER", "EMAIL"];

export const loginMethodTranslationKey: Record<UserLoginMethod["method"], TMessageKey> = {
  BANKID: "common.bankid",
  USERNAME: "ssoAccountSelection.method.usernamePassword",
  MICROSOFT: "ssoAccountSelection.method.microsoft",
  GOOGLE: "ssoAccountSelection.method.google",
};

export const isSsoMethodDeletable = (method: UserLoginMethod["method"]): boolean =>
  method === "MICROSOFT" || method === "GOOGLE";

export const userSettingsApi = {
  getUserSettings: ({
    onSuccess,
    onFailure,
  }: {
    onSuccess: (res: HttpResponse<GetUserSettingsResponse>, state: RootStoreData) => DCEvent[];
    onFailure: (err: unknown, state: RootStoreData) => DCEvent[];
  }): HttpEvent =>
    httpGet({
      url: "/user-settings",
      onSuccess,
      onFailure,
    }),

  getLoginMethods: ({
    onSuccess,
    onFailure,
  }: {
    onSuccess: (res: HttpResponse<UserLoginMethodsResponse>, state: RootStoreData) => DCEvent[];
    onFailure: (err: unknown, state: RootStoreData) => DCEvent[];
  }): HttpEvent =>
    httpGet({
      url: "/user-settings/login-methods",
      onSuccess,
      onFailure,
    }),

  updateEmail: ({
    data,
    onSuccess,
    onFailure,
  }: {
    data: UpdateEmailRequest;
    onSuccess: () => DCEvent[];
    onFailure: () => DCEvent[];
  }): HttpEvent =>
    httpPost({
      url: "/user-settings/update-email",
      data,
      onSuccess,
      onFailure,
    }),

  updatePhoneNumber: ({
    data,
    onSuccess,
    onFailure,
  }: {
    data: UpdatePhoneNumberRequest;
    onSuccess: () => DCEvent[];
    onFailure: () => DCEvent[];
  }): HttpEvent =>
    httpPost({
      url: "/user-settings/update-phone-number",
      data,
      onSuccess,
      onFailure,
    }),

  updateUsername: ({
    data,
    onSuccess,
    onFailure,
  }: {
    data: UpdateUsernameRequest;
    onSuccess: () => DCEvent[];
    onFailure: () => DCEvent[];
  }): HttpEvent =>
    httpPost({
      url: "/user-settings/update-username",
      data,
      onSuccess,
      onFailure,
    }),

  changePassword: ({
    data,
    onSuccess,
    onFailure,
  }: {
    data: ChangePasswordRequest;
    onSuccess: () => DCEvent[];
    onFailure: (err: unknown) => DCEvent[];
  }): HttpEvent =>
    httpPost({
      url: "/user-settings/change-password",
      data,
      onSuccess,
      onFailure,
    }),

  createPassword: ({
    data,
    onSuccess,
    onFailure,
  }: {
    data: { language: Language };
    onSuccess: () => DCEvent[];
    onFailure: () => DCEvent[];
  }): HttpEvent =>
    httpPost({
      url: "/user-settings/create-password",
      data,
      onSuccess,
      onFailure,
    }),

  deleteSso: ({
    method,
    onSuccess,
    onFailure,
  }: {
    method: UserLoginMethodType;
    onSuccess: () => DCEvent[];
    onFailure: () => DCEvent[];
  }): HttpEvent =>
    httpDelete({
      url: `/user-settings/sso/${method.toLowerCase()}`,
      onSuccess,
      onFailure,
    }),

  createApiKey: ({
    onSuccess,
    onFailure,
  }: {
    onSuccess: (res: HttpResponse<ApiKey>) => DCEvent[];
    onFailure: () => DCEvent[];
  }): HttpEvent =>
    httpPost({
      url: "/user-settings/create-api-key",
      onSuccess,
      onFailure,
    }),

  deleteApiKey: ({
    apiKey,
    onSuccess,
    onFailure,
  }: {
    apiKey: string;
    onSuccess: () => DCEvent[];
    onFailure: () => DCEvent[];
  }): HttpEvent =>
    httpDelete({
      url: `/user-settings/api-key/${apiKey}`,
      onSuccess,
      onFailure,
    }),
};

const updateUserSettingsState = (
  f: (state?: UserSettingsState) => Partial<UserSettingsState>,
): DCEvent => ({
  type: "update-store",
  data: {
    userSettings: (state) => ({
      ...state,
      ...f(state),
    }),
  },
});

const fetchLoginMethods = (): DCEvent =>
  userSettingsApi.getLoginMethods({
    onSuccess: (res) => [
      updateUserSettingsState(() => ({
        isLoading: false,
        loginMethods: res.data.loginMethods,
      })),
    ],
    onFailure: () => [
      updateUserSettingsState(() => ({
        isLoading: false,
        loginMethods: [],
      })),
      showGeneralError(),
    ],
  });

const fetchUserSettings = (referrer: string | undefined): DCEvent[] => [
  updateUserSettingsState(() => ({
    isLoading: true,
    referrer,
  })),
  userSettingsApi.getUserSettings({
    onSuccess: (res) => [
      updateUserSettingsState(() => ({
        isLoading: false,
        response: res.data,
      })),
      fetchLoginMethods(),
    ],
    onFailure: () => [
      updateUserSettingsState(() => ({
        isLoading: false,
      })),
      showGeneralError(),
    ],
  }),
];

export const initUserSettingsPage = (
  state: UserSettingsState | undefined,
  referrer: string | undefined,
): DCEvent[] => {
  if (state && state.referrer === referrer) {
    return [];
  }
  return fetchUserSettings(referrer);
};

export const mapToUserSettingsTab = (tab: string): UserSettingsTab => {
  switch (tab) {
    case "additional-emails":
      return "additional-emails";
    case "api-key":
      return "api-key";
    default:
      return "account";
  }
};

export const userSettingsActions = {
  selectTab: (tab: UserSettingsTab, referrer?: string): DCEvent[] => [
    navigate(userSettingsTabUrl(tab, referrer)),
  ],
};

const ssoFeedbackAlert = (params: QueryParams): DCEvent[] => {
  const connected = singleValue(params.sso_connected) ?? null;
  if (isSsoConnectedProvider(connected)) {
    return alertAction(createSsoConnectedAlert(connected));
  }
  const domain = singleValue(params.domain)?.trim();
  switch (singleValue(params.sso_error)) {
    case undefined:
      return [];
    case "domain-not-activated":
      return domain ? alertAction(createSsoDomainNotActivatedAlert(domain)) : [showGeneralError()];
    case "workspace-account-required":
      return [showError({ key: "sso.workspace-account-required.description" })];
    case "already-linked":
      return [
        showError({
          key: "sso.already-linked",
          params: {
            provider: singleValue(params.provider) === "microsoft" ? "Microsoft" : "Google",
          },
        }),
      ];
    default:
      return [showGeneralError()];
  }
};

export const showSsoFeedback = (tab: UserSettingsTab, params: QueryParams): DCEvent[] => {
  if (!params.sso_connected && !params.sso_error) {
    return [];
  }
  return [
    ...ssoFeedbackAlert(params),
    navigate(userSettingsTabUrl(tab, singleValue(params.referrer)), { replace: true }),
  ];
};

export const editEmail = (email: string): DCEvent[] => [
  updateUserSettingsState(() => ({
    emailForm: { email: createEmailField({ value: email, required: true }) },
  })),
];

export const cancelEditEmail = (): DCEvent[] => [
  updateUserSettingsState(() => ({
    emailForm: undefined,
  })),
];

const toUpdateEmailRequest = (form: EmailForm): UpdateEmailRequest | undefined => {
  form.email.commit();
  return form.email.validate() ? { email: form.email.validatedValue } : undefined;
};

export const saveEmail = (form: EmailForm | undefined, refreshUser: () => void): DCEvent[] => {
  const data = form && !form.isSaving ? toUpdateEmailRequest(form) : undefined;
  if (!form || !data) {
    return [];
  }
  return [
    updateUserSettingsState(() => ({
      emailForm: { ...form, isSaving: true },
    })),
    userSettingsApi.updateEmail({
      data,
      onSuccess: () => [
        updateUserSettingsState(() => ({
          emailForm: undefined,
        })),
        thunk(refreshUser),
      ],
      onFailure: () => [
        updateUserSettingsState(() => ({
          emailForm: undefined,
        })),
        showGeneralError(),
      ],
    }),
  ];
};

export const editPhoneNumber = (phoneNumber: PhoneNumber | undefined): DCEvent[] => [
  updateUserSettingsState(() => ({
    phoneNumberForm: { phoneNumber: new PhoneInputModel({ value: phoneNumber, required: true }) },
  })),
];

export const cancelEditPhoneNumber = (): DCEvent[] => [
  updateUserSettingsState(() => ({
    phoneNumberForm: undefined,
  })),
];

const toUpdatePhoneNumberRequest = (
  form: PhoneNumberForm,
): UpdatePhoneNumberRequest | undefined => {
  const phoneNumber = form.phoneNumber.toValidPhoneNumber();
  return phoneNumber ? { phoneNumber } : undefined;
};

export const savePhoneNumber = (
  form: PhoneNumberForm | undefined,
  refreshUser: () => void,
): DCEvent[] => {
  const data = form && !form.isSaving ? toUpdatePhoneNumberRequest(form) : undefined;
  if (!form || !data) {
    return [];
  }
  return [
    updateUserSettingsState(() => ({
      phoneNumberForm: { ...form, isSaving: true },
    })),
    userSettingsApi.updatePhoneNumber({
      data,
      onSuccess: () => [
        updateUserSettingsState(() => ({
          phoneNumberForm: undefined,
        })),
        thunk(refreshUser),
      ],
      onFailure: () => [
        updateUserSettingsState(() => ({
          phoneNumberForm: undefined,
        })),
        showGeneralError(),
      ],
    }),
  ];
};

export const saveUsernameType = (
  state: UserSettingsState,
  user: User | null,
  usernameType: UsernameType,
  refreshUser: () => void,
): DCEvent[] => {
  if (state.savingUsernameType || usernameType === currentUsernameType(user)) {
    return [];
  }
  return [
    updateUserSettingsState(() => ({
      savingUsernameType: usernameType,
    })),
    userSettingsApi.updateUsername({
      data: { usernameType },
      onSuccess: () => [
        updateUserSettingsState(() => ({
          savingUsernameType: undefined,
        })),
        thunk(refreshUser),
        showSuccess({ key: "user-settings.username-updated" }),
      ],
      onFailure: () => [
        updateUserSettingsState(() => ({
          savingUsernameType: undefined,
        })),
        showGeneralError(),
      ],
    }),
  ];
};

export function validatePassword(value: string): TMessage | undefined {
  if (value.length < 8 || !/[A-Z]/.test(value) || !/[a-z]/.test(value) || !/\d/.test(value)) {
    return { key: "user-settings.weak-password", params: { n: 8 } };
  }
}

export function makePasswordForm(): PasswordForm {
  const oldPasswordModel = new StringFieldModel({ required: true });
  const newPasswordModel = new StringFieldModel({
    required: true,
    validator: validatePassword,
  });
  const confirmedPasswordModel = new StringFieldModel({
    required: true,
    validator: (value) => {
      if (value !== newPasswordModel.inputValue) {
        return { key: "user-settings.not-matching" };
      }
    },
  });
  return {
    oldPassword: oldPasswordModel,
    newPassword: newPasswordModel,
    confirmedPassword: confirmedPasswordModel,
  };
}

export function toChangePasswordRequest(form: PasswordForm): ChangePasswordRequest | undefined {
  form.oldPassword.commit();
  form.newPassword.commit();
  form.confirmedPassword.commit();
  if (
    !form.oldPassword.validate() ||
    !form.newPassword.validate() ||
    !form.confirmedPassword.validate()
  ) {
    return;
  }
  return {
    oldPassword: form.oldPassword.validatedValue,
    newPassword: form.newPassword.validatedValue,
  };
}

export const editPassword = (): DCEvent[] => [
  updateUserSettingsState(() => ({
    passwordForm: makePasswordForm(),
  })),
];

export const cancelEditPassword = (): DCEvent[] => [
  updateUserSettingsState(() => ({
    passwordForm: undefined,
  })),
];

const isOldPasswordIncorrect = (err: unknown): boolean =>
  isAxiosError(err) && get(err, ["response", "data", "error"]) === "oldPasswordIncorrect";

const onChangePasswordFailure = (form: PasswordForm, err: unknown): DCEvent[] => {
  if (isOldPasswordIncorrect(err)) {
    form.oldPassword.setError({ key: "user-settings.old-password-incorrect" });
    return [
      updateUserSettingsState(() => ({
        passwordForm: { ...form, isSaving: false },
      })),
    ];
  }
  return [
    updateUserSettingsState(() => ({
      passwordForm: undefined,
    })),
    showGeneralError(),
  ];
};

export const savePassword = (form: PasswordForm | undefined): DCEvent[] => {
  const data = form && !form.isSaving ? toChangePasswordRequest(form) : undefined;
  if (!form || !data) {
    return [];
  }
  return [
    updateUserSettingsState(() => ({
      passwordForm: { ...form, isSaving: true },
    })),
    userSettingsApi.changePassword({
      data,
      onSuccess: () => [
        updateUserSettingsState(() => ({
          passwordForm: undefined,
        })),
      ],
      onFailure: (err) => onChangePasswordFailure(form, err),
    }),
  ];
};

export const createPassword = (language: Language): DCEvent[] => [
  updateUserSettingsState(() => ({ createPasswordResult: pending() })),
  userSettingsApi.createPassword({
    data: { language },
    onSuccess: () => [updateUserSettingsState(() => ({ createPasswordResult: ok() }))],
    onFailure: () => [
      updateUserSettingsState(() => ({ createPasswordResult: undefined })),
      showGeneralError(),
    ],
  }),
];

export const connectSso = (
  method: UserLoginMethodType,
  referrer: string | undefined,
): DCEvent[] => [
  loadUrl(
    `/api/auth/${method.toLowerCase()}${toQueryString({
      mode: "CONNECT",
      returnTo: userSettingsTabUrl("account", referrer),
    })}`,
    false,
  ),
];

export const deleteSso = (method: UserLoginMethodType): DCEvent[] => [
  updateUserSettingsState(() => ({ deletingSsoMethod: method })),
  userSettingsApi.deleteSso({
    method,
    onSuccess: () => [
      updateUserSettingsState(() => ({ deletingSsoMethod: undefined })),
      fetchLoginMethods(),
    ],
    onFailure: () => [
      updateUserSettingsState(() => ({ deletingSsoMethod: undefined })),
      showGeneralError(),
    ],
  }),
];

export const createApiKey = (): DCEvent[] => [
  updateUserSettingsState(() => ({ isCreatingApiKey: true })),
  userSettingsApi.createApiKey({
    onSuccess: (res) => [
      showGeneralSuccess(),
      updateUserSettingsState((state) => ({
        isCreatingApiKey: false,
        newApiKey: res.data,
        response: { ...state?.response, apiKeys: [...(state?.response?.apiKeys ?? []), res.data] },
      })),
    ],
    onFailure: () => [
      updateUserSettingsState(() => ({ isCreatingApiKey: false })),
      showGeneralError(),
    ],
  }),
];

export const deleteApiKey = (apiKey: string): DCEvent[] => [
  updateUserSettingsState(() => ({ deletingApiKey: apiKey })),
  userSettingsApi.deleteApiKey({
    apiKey,
    onSuccess: () => [
      showGeneralSuccess(),
      updateUserSettingsState((state) => ({
        deletingApiKey: undefined,
        newApiKey: state?.newApiKey?.key === apiKey ? undefined : state?.newApiKey,
        response: {
          ...state?.response,
          apiKeys: (state?.response?.apiKeys ?? []).filter((k) => k.key !== apiKey),
        },
      })),
    ],
    onFailure: () => [
      updateUserSettingsState(() => ({ deletingApiKey: undefined })),
      showGeneralError(),
    ],
  }),
];

export const hideNewApiKey = (): DCEvent[] => [
  updateUserSettingsState(() => ({ newApiKey: undefined })),
];

const userSettingsTabs = ({
  tab,
  dispatch,
  referrer,
  additionalEmails,
}: {
  tab: UserSettingsTab;
  dispatch: EventDispatcher;
  referrer: string | undefined;
  additionalEmails?: AdditionalUserWithEmail[];
}): TabButtonProps[] => {
  return compact([
    {
      id: "account",
      label: { key: "user-settings.account" },
      isActive: tab === "account",
      onSelect: () => dispatch(userSettingsActions.selectTab("account", referrer)),
    },
    additionalEmails &&
      additionalEmails.length >= 1 && {
        id: "additional-emails",
        label: { key: "user-settings.additional-emails" },
        tag: { label: String(additionalEmails.length), color: "info" },
        isActive: tab === "additional-emails",
        onSelect: () => dispatch(userSettingsActions.selectTab("additional-emails", referrer)),
      },
    {
      id: "api-key",
      label: { key: "user-settings.api-key" },
      isActive: tab === "api-key",
      onSelect: () => dispatch(userSettingsActions.selectTab("api-key", referrer)),
    },
  ]);
};

export const additionalEmailsInfo = (
  additionalEmails: AdditionalUserWithEmail[],
  referrer: string | undefined,
): TMessage | undefined => {
  if (additionalEmails.length === 0) {
    return undefined;
  }
  return {
    key: "user-settings.additional-emails.info",
    params: {
      count: additionalEmails.length,
      companyCount: uniqBy(additionalEmails, (e) => e.company.organisationNumber).length,
      additionalEmailsUrl: userSettingsTabUrl("additional-emails", referrer),
    },
  };
};

export function maskLastDigits(value: string, count: number): string {
  if (value.length <= count) {
    return "*".repeat(value.length);
  }
  return value.slice(0, value.length - count) + "*".repeat(count);
}

export const currentUsernameType = (user: User | null): UsernameType =>
  user?.email && user.userName === user.email ? "EMAIL" : "SOCIAL_SECURITY_NUMBER";

const socialSecurityNumberUsername = (user: User | null): string => {
  const value =
    currentUsernameType(user) === "EMAIL"
      ? (user?.socialSecurityNumber ?? "")
      : (user?.userName ?? "");
  return norwegianPersonalIdValidator(value) === undefined ? maskLastDigits(value, 5) : value;
};

const usernameLabel = (option: UsernameType, user: User | null): TMessage => {
  switch (option) {
    case "SOCIAL_SECURITY_NUMBER":
      return { translated: socialSecurityNumberUsername(user) };
    case "EMAIL":
      return user?.email ? { translated: user.email } : { key: "user-settings.missing-email" };
  }
};

const isEmailUsable = (user: User | null): boolean => Boolean(user?.email && user.isEmailVerified);

const lastLoginValue = (row: UserLoginMethod, referrer: string | undefined): TableColumnValue => {
  if (!row.isConnected && isSsoMethodDeletable(row.method)) {
    return [
      "link",
      {
        label: {
          key: "activate",
          params: { name: { key: loginMethodTranslationKey[row.method] } },
        },
        onClick: connectSso(row.method, referrer),
      },
    ] satisfies ["link", LinkButtonProps];
  }
  return row.lastUsed ? { type: "datetime", value: row.lastUsed } : "-";
};

const deleteSsoAction = (
  row: UserLoginMethod,
  deletingSsoMethod: UserLoginMethodType | undefined,
): RowAction | undefined => {
  if (!row.isConnected || !isSsoMethodDeletable(row.method)) {
    return undefined;
  }
  return [
    "icon",
    {
      icon: "trash",
      color: "error",
      compact: true,
      tooltip: { key: "delete" },
      loading: deletingSsoMethod === row.method,
      disabled: Boolean(deletingSsoMethod) && deletingSsoMethod !== row.method,
      onClick: confirmAction({
        title: { key: "confirm-delete" },
        text: {
          key: "general.confirm-delete",
          params: { name: { key: loginMethodTranslationKey[row.method] } },
        },
        confirmLabel: { key: "delete" },
        onConfirm: deleteSso(row.method),
      }),
    },
  ] satisfies ["icon", IconButtonProps];
};

export const maskApiKey = (key: string): string => `${key.slice(0, 4)}**************`;

const copyApiKeyAction = (row: ApiKey): RowAction =>
  [
    "icon",
    {
      icon: "copy",
      compact: true,
      tooltip: { key: "general.copy-to-clipboard" },
      onClick: [copyToClipboard(row.key)],
    },
  ] satisfies ["icon", IconButtonProps];

const deleteApiKeyAction = (row: ApiKey, deletingApiKey: string | undefined): RowAction =>
  [
    "icon",
    {
      icon: "trash",
      color: "error",
      compact: true,
      tooltip: { key: "delete" },
      loading: deletingApiKey === row.key,
      disabled: Boolean(deletingApiKey) && deletingApiKey !== row.key,
      onClick: confirmAction({
        title: { key: "confirm-delete" },
        text: {
          key: "general.confirm-delete",
          params: { name: { key: "user-settings.api-key.title" } },
        },
        confirmLabel: { key: "delete" },
        onConfirm: deleteApiKey(row.key),
      }),
    },
  ] satisfies ["icon", IconButtonProps];

export const prepUserSettings = ({
  messageStore,
  userSettingsTab,
  dispatch,
  state,
  user,
  isNorwegianAuthenticatedCountry,
  refreshUser,
}: {
  messageStore: AlertMessageStore;
  userSettingsTab: UserSettingsTab;
  dispatch: EventDispatcher;
  state: UserSettingsState;
  user: User | null;
  isNorwegianAuthenticatedCountry: boolean;
  refreshUser: () => void;
}): UserSettingsProps => {
  return {
    messageStore,
    userSettingsTab,
    name: getFullName(user),
    navigateBack: [navigate(state.referrer || portfolioUrl)],
    tabs: userSettingsTabs({
      tab: userSettingsTab,
      dispatch,
      referrer: state.referrer,
      additionalEmails: state.response?.additionalUsersWithEmail,
    }),
    state,
    user,
    dispatch,
    additionalEmailInfo: additionalEmailsInfo(
      state.response?.additionalUsersWithEmail ?? [],
      state.referrer,
    ),
    isNorwegianAuthenticatedCountry,
    refreshUser,
    usernameTypeModel: prepUsernameTypeModel(state, user, dispatch, refreshUser),
    openCreteApiDialog: [],
  };
};

export const prepEmailField = (
  state: UserSettingsState,
  user: User | null,
  dispatch: EventDispatcher,
  refreshUser: () => void,
): EditableFieldProps => ({
  label: { key: "email" },
  value: user?.email ?? "",
  editModel: state.emailForm && ["input", state.emailForm.email],
  isSubmitting: state.emailForm?.isSaving,
  onEdit: () => dispatch(editEmail(user?.email ?? "")),
  onCancelEdit: () => dispatch(cancelEditEmail()),
  onSubmitEdit: () => dispatch(saveEmail(state.emailForm, refreshUser)),
  tag: prepEmailVerifiedTag(state, user),
});

const prepEmailVerifiedTag = (
  state: UserSettingsState,
  user: User | null,
): TagProps | undefined => {
  if (state.emailForm || !user?.email) {
    return undefined;
  }
  return user.isEmailVerified
    ? { label: { key: "user-settings.email-verified" }, color: "success" }
    : { label: { key: "user-settings.email-not-verified" }, color: "error" };
};

export const prepVerifyEmailButton = (
  state: UserSettingsState,
  user: User | null,
): ButtonProps | undefined => {
  if (state.emailForm || !user?.email || user.isEmailVerified) {
    return undefined;
  }
  return {
    variant: "primary",
    label: { key: "user-settings.email-verify" },
    onClick: [showVerifyEmailDialog],
  };
};

export const prepPhoneNumberField = (
  state: UserSettingsState,
  user: User | null,
  dispatch: EventDispatcher,
  refreshUser: () => void,
): EditableFieldProps => ({
  label: { key: "phone-number-field.number-label" },
  value: formatPhoneNumber(user?.phoneNumber),
  editModel: state.phoneNumberForm && ["phone", state.phoneNumberForm.phoneNumber],
  isSubmitting: state.phoneNumberForm?.isSaving,
  onEdit: () => dispatch(editPhoneNumber(user?.phoneNumber)),
  onCancelEdit: () => dispatch(cancelEditPhoneNumber()),
  onSubmitEdit: () => dispatch(savePhoneNumber(state.phoneNumberForm, refreshUser)),
});

export const prepUsernameTypeModel = (
  state: UserSettingsState,
  user: User | null,
  dispatch: EventDispatcher,
  refreshUser: () => void,
): RadioFieldModel<UsernameType> =>
  new RadioFieldModel<UsernameType>({
    value: state.savingUsernameType ?? currentUsernameType(user),
    options: usernameTypes,
    idFn: (option) => option,
    labelFn: (option) => usernameLabel(option, user),
    descriptionFn: (option) =>
      option === "EMAIL" && user?.email && !user.isEmailVerified
        ? { key: "user-settings.email-not-verified" }
        : undefined,
    disabledFn: (option) =>
      Boolean(state.savingUsernameType) || (option === "EMAIL" && !isEmailUsable(user)),
    onChange: (option) => dispatch(saveUsernameType(state, user, option, refreshUser)),
  });

export const prepPasswordField = (
  state: UserSettingsState,
  dispatch: EventDispatcher,
  user: User | null,
): PasswordFieldProps =>
  state.passwordForm
    ? {
        mode: "edit",
        form: state.passwordForm,
        onSave: () => dispatch(savePassword(state.passwordForm)),
        onCancel: () => dispatch(cancelEditPassword()),
      }
    : {
        mode: "show",
        hasPassword: Boolean(user?.hasPassword),
        isEmailAvailable: Boolean(user?.email),
        hasCreatedPassword: isOk(state.createPasswordResult),
        isLoading: isPending(state.createPasswordResult),
        onEdit: () => dispatch(editPassword()),
        createPassword: () => user && dispatch(createPassword(toSupportedLanguage(user.language))),
      };

export const prepLoginMethodTable = (
  state: UserSettingsState,
): DataTableProps<UserLoginMethod> => ({
  tableData: state.loginMethods ?? [],
  variant: "IN_CARD",
  getId: (row) => row.method,
  tableColumns: [
    {
      name: "method",
      label: { key: "user-settings.login-method" },
      align: "left",
      notSorted: true,
      getValue: (row) => ({ key: loginMethodTranslationKey[row.method] }),
    },
    {
      name: "lastUsed",
      label: { key: "last-login" },
      notSorted: true,
      getValue: (row) => lastLoginValue(row, state.referrer),
    },
  ],
  rowActions: (row) => compact([deleteSsoAction(row, state.deletingSsoMethod)]),
});

export const prepApiKeyTable = (state: UserSettingsState): DataTableProps<ApiKey> => ({
  tableData: state.response?.apiKeys ?? [],
  variant: "IN_CARD",
  getId: (row) => row.key,
  tableColumns: compact([
    {
      name: "title",
      align: "left",
      label: { key: "user-settings.api-key.title" },
      getValue: (row) => maskApiKey(row.key),
      notSorted: true,
    },
    {
      name: "status",
      align: "left",
      label: { key: "user-settings.api-key.active" },
      getValue: (row) => ({ key: row.isActive ? "yes" : "no" }),
      notSorted: true,
    },
    {
      name: "expiresAt",
      align: "left",
      label: { key: "user-settings.api-key.expires-at" },
      getValue: (row, _, { f }) => f.formatDate(row.expiresAt),
      notSorted: true,
    },
  ]),
  rowActions: (row) => [copyApiKeyAction(row), deleteApiKeyAction(row, state.deletingApiKey)],
});

const adminInfo = (row: AdditionalUserWithEmail): ["info", InfoButtonProps] => [
  "info",
  {
    infoText: {
      key: "user-settings.additional-email.admin-info",
      params: {
        companyName: row.company.companyName,
        admins: {
          type: "md-list",
          value: row.adminsInCompany.map((a) => `${a.name} (${a.email})`),
        },
      },
    },
  },
];

export const prepAdditionalEmailTable = (
  state: UserSettingsState,
): DataTableProps<AdditionalUserWithEmail> => ({
  tableData: state.response?.additionalUsersWithEmail ?? [],
  variant: "IN_CARD_LARGE",
  getId: (row) => `${row.company.organisationNumber}-${row.role}-${row.email}`,
  tableColumns: compact([
    {
      name: "email",
      align: "left",
      label: { key: "user-settings.additional-email-address" },
      getValue: (row) => (row.email ? row.email : { key: "user-settings.additional-emails.none" }),
      getSortValue: (row) => row.email,
      getClassName: (row) => (row.email ? undefined : "italic color-error"),
    },
    {
      name: "company",
      align: "left",
      label: { key: "user-settings.additional-email-company" },
      getValue: (row) => row.company.companyName,
      getSortValue: (row) => row.company.companyName,
    },
    {
      name: "role",
      align: "left",
      label: { key: "user-settings.additional-email-role" },
      getValue: (row) => ({ key: accessLabels[row.role] }),
      getSortValue: (row) => ({ key: accessLabels[row.role] }),
    },
    {
      name: "info",
      label: "",
      getValue: (row) => (row.adminsInCompany.length > 0 ? adminInfo(row) : undefined),
    },
  ]),
});
